import * as THREE from "three";

export interface VehicleTextures {
  paint: THREE.Texture;
  glass: THREE.Texture;
  tire: THREE.Texture;
  taxiLivery: THREE.Texture;
  policeLivery: THREE.Texture;
}

const TEXTURE_SPECS = [
  ["paint", "/textures/vehicles/paint.svg", THREE.RepeatWrapping, 1 / 0.75],
  ["glass", "/textures/vehicles/glass.svg", THREE.RepeatWrapping, 1],
  ["tire", "/textures/vehicles/tire.svg", THREE.RepeatWrapping, 2],
  ["taxiLivery", "/textures/vehicles/taxi-livery.svg", THREE.ClampToEdgeWrapping, 1],
  ["policeLivery", "/textures/vehicles/police-livery.svg", THREE.ClampToEdgeWrapping, 1],
] as const satisfies ReadonlyArray<
  readonly [key: keyof VehicleTextures, url: string, wrapping: THREE.Wrapping, repeat: number]
>;

export async function loadVehicleTextures(maxAnisotropy: number): Promise<VehicleTextures> {
  const loader = new THREE.TextureLoader();
  const results = await Promise.allSettled(
    TEXTURE_SPECS.map(async ([, url, wrapping, repeat]) => {
      const texture = await loader.loadAsync(url);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = wrapping;
      texture.wrapT = wrapping;
      texture.repeat.set(repeat, repeat);
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = Math.min(8, maxAnisotropy);
      return texture;
    }),
  );

  const failedIndex = results.findIndex((result) => result.status === "rejected");
  if (failedIndex !== -1) {
    for (const result of results) {
      if (result.status === "fulfilled") result.value.dispose();
    }
    throw new Error(`Vehicle texture failed to load: ${TEXTURE_SPECS[failedIndex]![1]}`);
  }

  const loaded = results.map((result) => (result as PromiseFulfilledResult<THREE.Texture>).value);
  return {
    paint: loaded[0]!,
    glass: loaded[1]!,
    tire: loaded[2]!,
    taxiLivery: loaded[3]!,
    policeLivery: loaded[4]!,
  };
}

export function disposeVehicleTextures(textures: VehicleTextures): void {
  for (const texture of Object.values(textures)) texture.dispose();
}
